import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { NavBar } from "../navbar/navbar";
import { Markdown } from "../forum/Markdown";
import { useLanguage } from "../../i18n/LanguageProvider";
import { usePermissions } from "../../hooks/usePermissions";
import {
  Article,
  ArticleStatus,
  ArticleSummary,
  useArticlesApi,
  useCoverUrl,
} from "./articlesApi";
import "../../styles/sp-ui.css";
import "../forum/forum.css";
import "./articles.css";

// Artiklar: long-form monthly stories (Mistral Large text, generated cover).
// While in testing only admins (articles:read) see the section; managers
// (articles:manage) also get the publish / unpublish / remove controls.

const readingMinutes = (words: number | null) => Math.max(1, Math.round((words ?? 0) / 220));

const useDate = () => {
  const { locale } = useLanguage();
  return useCallback(
    (iso: string | null) =>
      iso ? new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }) : "",
    [locale],
  );
};

// Body: forum-style safe Markdown, plus ## / ### subheads (split out here so the
// forum renderer stays unchanged). Nothing is injected as HTML.
const ArticleBody: React.FC<{ text: string }> = ({ text }) => {
  const parts = useMemo(() => {
    const out: { kind: "h2" | "h3" | "md"; text: string }[] = [];
    let buf: string[] = [];
    const flush = () => {
      if (buf.join("").trim()) out.push({ kind: "md", text: buf.join("\n") });
      buf = [];
    };
    for (const line of text.replace(/\r\n/g, "\n").split("\n")) {
      const m = /^(#{1,3})\s+(.+?)\s*#*\s*$/.exec(line);
      if (m) {
        flush();
        out.push({ kind: m[1].length >= 3 ? "h3" : "h2", text: m[2] });
      } else {
        buf.push(line);
      }
    }
    flush();
    return out;
  }, [text]);

  return (
    <div className="article-body">
      {parts.map((p, i) =>
        p.kind === "h2" ? (
          <h2 key={i}>{p.text}</h2>
        ) : p.kind === "h3" ? (
          <h3 key={i}>{p.text}</h3>
        ) : (
          <Markdown key={i} text={p.text} className="article-md" />
        ),
      )}
    </div>
  );
};

const StatusChip: React.FC<{ status: ArticleStatus }> = ({ status }) => {
  const { t } = useLanguage();
  return <span className={`article-status article-status--${status}`}>{t(`articles.status.${status}`)}</span>;
};

// Publish / unpublish / remove / delete — shared by the article page and the admin list.
export const ArticleActions: React.FC<{
  article: ArticleSummary;
  onChange: (a: ArticleSummary | null) => void;
  compact?: boolean;
}> = ({ article, onChange, compact }) => {
  const { t } = useLanguage();
  const api = useArticlesApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<ArticleSummary | null>) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await fn());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const setStatus = (s: ArticleStatus) => run(() => api.setStatus(article.id, s));

  return (
    <div className={`article-actions ${compact ? "is-compact" : ""}`}>
      {article.status !== "published" && (
        <button className="sp-btn sp-btn--primary sp-btn--sm" disabled={busy} onClick={() => setStatus("published")}>
          {t("articles.publish")}
        </button>
      )}
      {article.status === "published" && (
        <button className="sp-btn sp-btn--sm" disabled={busy} onClick={() => setStatus("draft")}>
          {t("articles.unpublish")}
        </button>
      )}
      {article.status !== "removed" && (
        <button
          className="sp-btn sp-btn--danger sp-btn--sm"
          disabled={busy}
          onClick={() => window.confirm(t("articles.confirmRemove", { title: article.title })) && setStatus("removed")}
        >
          {t("articles.remove")}
        </button>
      )}
      {article.status === "removed" && (
        <button
          className="sp-btn sp-btn--danger sp-btn--sm"
          disabled={busy}
          onClick={() =>
            window.confirm(t("articles.confirmDelete", { title: article.title })) &&
            run(async () => {
              await api.remove(article.id);
              return null;
            })
          }
        >
          {t("articles.deleteForever")}
        </button>
      )}
      {error && <span className="article-actions__error">{error}</span>}
    </div>
  );
};

const ArticleCard: React.FC<{ a: ArticleSummary; lead?: boolean; showStatus?: boolean }> = ({ a, lead, showStatus }) => {
  const { t } = useLanguage();
  const date = useDate();
  const cover = useCoverUrl(a.id, a.hasCover);
  return (
    <Link to={`/artiklar/${a.slug}`} className={`article-card ${lead ? "article-card--lead" : ""}`}>
      <div className="article-card__img">
        {cover ? <img src={cover} alt={a.coverAlt ?? ""} /> : <div className="article-card__ph" />}
      </div>
      <div className="article-card__text">
        <div className="article-kicker">
          {t("articles.kicker")}
          {showStatus && a.status !== "published" && <StatusChip status={a.status} />}
        </div>
        <h2 className="article-card__title">{a.title}</h2>
        {a.dek && <p className="article-card__dek">{a.dek}</p>}
        <div className="article-meta">
          {date(a.publishedAt ?? a.createdAt)} · {t("articles.minutes", { count: readingMinutes(a.wordCount) })}
        </div>
      </div>
    </Link>
  );
};

export const ArticleListPage: React.FC = () => {
  const { t } = useLanguage();
  const api = useArticlesApi();
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("articles", "read");
  // Managers also see their drafts here (marked), so an upload shows up right away.
  const canManage = hasPermission("articles", "manage");
  const [articles, setArticles] = useState<ArticleSummary[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canRead) return;
    api
      .list(canManage)
      .then((r) => setArticles((r.articles ?? []).filter((a) => a.status !== "removed")))
      .catch(() => setError(true));
  }, [api, canRead, canManage]);

  const [lead, ...rest] = articles ?? [];
  return (
    <>
      <NavBar />
      <main className="fm-page articles-page">
        <section className="fm-hero">
          <div className="fm-hero__glow" />
          <div className="fm-hero__grid" />
          <div className="fm-hero__inner">
            <div className="fm-hero__text">
              <span className="fm-eyebrow">{t("articles.beta")}</span>
              <h1 className="fm-hero__title">{t("articles.title")}</h1>
              <p className="fm-hero__lead">{t("articles.subtitle")}</p>
            </div>
          </div>
        </section>
        {!canRead ? (
          <div className="art-empty">{t("articles.noAccess")}</div>
        ) : error ? (
          <div className="art-empty">{t("articles.loadError")}</div>
        ) : !articles ? (
          <div className="art-empty"><span className="sp-spinner" /></div>
        ) : articles.length === 0 ? (
          <div className="art-empty">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Z" /><path d="M8 8h8M8 12h8M8 16h5" /></svg>
            <span>{t("articles.empty")}</span>
          </div>
        ) : (
          <>
            <ArticleCard a={lead} lead showStatus={canManage} />
            {rest.length > 0 && (
              <div className="articles-grid">
                {rest.map((a) => <ArticleCard key={a.id} a={a} showStatus={canManage} />)}
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
};

export const ArticlePage: React.FC = () => {
  const { slug = "" } = useParams();
  const { t } = useLanguage();
  const api = useArticlesApi();
  const navigate = useNavigate();
  const date = useDate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("articles", "manage");
  const [article, setArticle] = useState<Article | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "missing">("loading");
  const cover = useCoverUrl(article?.id, !!article?.hasCover);

  useEffect(() => {
    setState("loading");
    api
      .get(slug)
      .then((a) => {
        if (!a || a.error || !a.id) throw new Error("missing");
        setArticle(a);
        setState("ok");
      })
      .catch(() => setState("missing"));
  }, [api, slug]);

  const sources = article?.sources ?? [];

  return (
    <>
      <NavBar />
      <main className="fm-page articles-page article-page">
        <Link to="/artiklar" className="article-back">← {t("articles.title")}</Link>
        {state === "loading" && <div className="art-empty"><span className="sp-spinner" /></div>}
        {state === "missing" && <div className="art-empty">{t("articles.notFound")}</div>}
        {state === "ok" && article && (
          <article className="article-sheet">
            {canManage && (
              <div className="article-admin">
                <StatusChip status={article.status} />
                <span className="article-admin__label">{t("articles.adminOnly")}</span>
                <ArticleActions
                  article={article}
                  onChange={(a) => (a ? setArticle({ ...article, ...a }) : navigate("/artiklar"))}
                />
              </div>
            )}
            <header className="article-header">
              <div className="article-kicker">{t("articles.kicker")}</div>
              <h1 className="article-title">{article.title}</h1>
              {article.dek && <p className="article-dek">{article.dek}</p>}
              <div className="article-meta">
                {t("articles.byline")} · {date(article.publishedAt ?? article.createdAt)} ·{" "}
                {t("articles.minutes", { count: readingMinutes(article.wordCount) })}
              </div>
            </header>
            {article.hasCover && (
              <figure className="article-cover">
                {cover ? <img src={cover} alt={article.coverAlt ?? ""} /> : <div className="article-card__ph" />}
                {article.coverAlt && <figcaption>{t("articles.coverCaption")}</figcaption>}
              </figure>
            )}
            <ArticleBody text={article.bodyMd} />
            <footer className="article-foot">
              <p className="article-disclosure">{t("articles.disclosure")}</p>
              {sources.length > 0 && (
                <div className="article-sources">
                  <h4>{t("articles.sources")}</h4>
                  <ul>
                    {sources.map((s, i) => (
                      <li key={i}>
                        <strong>{s.label}</strong>
                        {s.detail ? ` — ${s.detail}` : ""}
                        {s.url && /^https?:\/\//.test(s.url) && (
                          <>
                            {" "}
                            <a href={s.url} target="_blank" rel="noopener noreferrer">{t("articles.link")}</a>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </footer>
          </article>
        )}
      </main>
    </>
  );
};
